import { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row, Stack } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";

import useScriptsStore from "../stores/Scripts";
import useUserStore from "../stores/User";

import { getScripts } from "../utils/GetScripts";
import Indexing from "../utils/Indexing";
import { populateListsFromView, getTagFolders } from "../utils/Views";
import { getFileMimeType, getIndexerSignedURL } from "../utils/Storage";
import { showToast } from "../utils/Toasts";
import { useDebounce } from "../utils/useDebounce";
import { truncateMiddle } from "../utils/Text";
import { fetchGenresFromWikipedia } from "../utils/WikipediaGenre";

import { ConfirmationModal, RequestMatchConfirmationModal } from "./Confirmation";
import FileNameForceWrap from "./FileNameForceWrap";
import PDFViewer from "./PdfViewer";
import AltViewer from "./AltViewer";
import { LoadingRing } from "./LoadingRing";

// Initial form values
const INITIAL_FORM_VALUES = {
    script_title: '',
    script_writers: '',
    draft_date: '',
    document_type: '',
    document_version: '',
    alternate_titles: '',
    episode_number: '',
    document_notes: '',
    tags: [] as string[],
};

interface IndexingEntryFormProps {
    url: string;
    currentRow: any;
    currentFileList: any[];
    setShowForm: (show: boolean) => void;
    setParentLoadingState: (loading: boolean) => void;
    refreshFileList?: () => Promise<any>;
}

interface DatabaseScript {
    "Title": string;
    "Script Title": string;
    "Writer(s)"?: string;
    "Category"?: string;
    google_drive_hash?: string;
    storage_hash?: string;
}

export function IndexingEntryForm({ url, currentRow, currentFileList, setShowForm, setParentLoadingState, refreshFileList }: IndexingEntryFormProps) {
    const userData = useUserStore((state) => state.data);

    const totalScripts = useScriptsStore((state) => state.totalScripts) as DatabaseScript[];
    const setTotalScripts = useScriptsStore((state) => state.setTotalScripts);
    const setTotalScriptsCount = useScriptsStore((state) => state.setTotalCount);

    const [lists, setLists] = useState({});
    const [hasLists, setHasLists] = useState(false);
    const [tagFolders, setTagFolders] = useState<{ id: number; name: string; tags: string[] }[]>([]);

    const [isTV, setIsTV] = useState(false);
    const [fileType, setFileType] = useState(currentRow?.file_type === 'application/pdf' ? 'pdf' : 'other');
    const [validated, setValidated] = useState(false);
    const [additionalInfo, setAdditionalInfo] = useState('');
    const [showDownloadLink, setShowDownloadLink] = useState(false);

    const [indexingMatches, setIndexingMatches] = useState([]);
    const [databaseMatches, setDatabaseMatches] = useState([]);
    const [titleMatches, setTitleMatches] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [pendingAction, setPendingAction] = useState<((match?: any) => Promise<void>) | null>(null);

    const [formData, setFormData] = useState({ ...INITIAL_FORM_VALUES });
    const resetForm = () => {
        setFormData({ ...INITIAL_FORM_VALUES });
    };

    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestMatches, setRequestMatches] = useState([]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            episode_number: isTV ? prev.episode_number : '',
            [name]: typeof value === 'string' ? value : value['label']
        }));

        // Reset validation when user starts typing
        if (validated) {
            setValidated(false);
        }
    };

    // Validation function
    const validateForm = () => {
        const requiredFields = ['script_title', 'script_writers'];
        const isFormValid = requiredFields.every(field => {
            const value = formData[field];
            return value && value.trim() !== '';
        });
        
        // Check if date is valid
        const isDateValid = Indexing.validateDate(formData.draft_date);
        
        return isFormValid && isDateValid;
    };

    useEffect(() => {
        const infoFields = [
            formData.document_type,
            formData.document_version,
            formData.alternate_titles,
            formData.episode_number,
            formData.document_notes
        ].filter(field => field && field.trim() !== '');

        setAdditionalInfo(infoFields.join(', '));
    }, [formData]);

    // Prevent flashes of results before user finishes typing
    const debouncedTitle = useDebounce(formData.script_title, 300);

    useEffect(() => {
        // Only search for title matches if we have no exact matches
        if (indexingMatches.length === 0 && databaseMatches.length === 0) { 
            // Check for title matches when script_title changes
            Indexing.findTitleMatches(debouncedTitle, totalScripts, userData?.user_id).then(matches => {
                setTitleMatches(matches);
            }).catch(error => {
                console.error('Error finding title matches:', error);
                setTitleMatches([]);
            });
        }
    }, [debouncedTitle, totalScripts, indexingMatches, databaseMatches]);

    useEffect(() => {
        if (totalScripts.length === 0 && userData) {
            getScripts().then((data) => {
                if(data) {
                    setTotalScripts(data as []);
                    setTotalScriptsCount(data.length);
                } else {
                    showToast("error", "Unable to get scripts from database!");
                };
            });
        }
    }, [totalScripts.length, setTotalScripts, setTotalScriptsCount, userData]);

    useEffect(() => {
        async function fetchLists() {
            const [listsFromView, folders] = await Promise.all([
                populateListsFromView(['Document Type', 'Draft']),
                getTagFolders(),
            ]);
            if (listsFromView) {
                setLists(listsFromView);
                setHasLists(true);
            } else {
                showToast("error", "Unable to load lists.");
            }
            setTagFolders(folders);
        }

        if (!hasLists) {
            fetchLists();
        }
    }, [hasLists]);

    useEffect(() => {        
        const determineFileType = async () => {
            // First, check if we have MIME type in currentRow.file_type
            if (currentRow.file_type) {
                setFileType(currentRow.file_type === 'application/pdf' ? 'pdf' : 'other');
                return;
            }

            // Fallback: get MIME type from Supabase storage
            try {
                const mimeType = await getFileMimeType(currentRow.file_name);
                setFileType(mimeType === 'application/pdf' ? 'pdf' : 'other');
            } catch (error) {
                setFileType(Indexing.getFileType(url));
            }
        };

        if (currentRow?.file_name) {
            determineFileType();
        }
    }, [currentRow?.file_name, currentRow?.file_type, url]);

    // Load currentRow data into form when available
    useEffect(() => {
        if (currentRow && Object.keys(currentRow).length > 0) {

            // Map database fields to form fields
            const loadedFormData = { ...INITIAL_FORM_VALUES };
            
            // Populate form fields from currentRow data
            Object.keys(INITIAL_FORM_VALUES).forEach(field => {
                loadedFormData[field] = field === 'tags' ? (currentRow[field] || []) : (currentRow[field] || '');
            });
            
            setFormData(loadedFormData);

            // Check for storage and database matches via file_hash
            if (currentRow.file_hash) {
                const indexingMatchesFound = currentFileList.filter(
                    file => (file.file_hash === currentRow.file_hash) && // same hash AND
                            (file.file_name !== currentRow.file_name) // different file name
                );
                if (indexingMatchesFound.length > 0) {
                    const matchesWithUrls = indexingMatchesFound.map(async match => {
                        const signedURL = await getIndexerSignedURL(match.file_name, userData?.user_id);
                        return { ...match, signed_url: signedURL };
                    });
                    Promise.all(matchesWithUrls).then(results => {
                        setIndexingMatches(results);
                    }).catch(error => {
                        console.error('Error getting signed URLs for indexing matches:', error);
                        setIndexingMatches(indexingMatchesFound);
                    });
                }

                const databaseMatchesFound = totalScripts.filter(
                    script => {
                        return (script.google_drive_hash === currentRow.file_hash) || // Google Drive hash match OR
                               (script.storage_hash === currentRow.file_hash) // Supabase storage hash match
                    }
                );
                if (databaseMatchesFound.length > 0) {
                    const matchesWithUrls = databaseMatchesFound.map(async match => {
                        const signedURL = await getIndexerSignedURL(match.Title, userData?.user_id);
                        return { ...match, signed_url: signedURL };
                    });
                    Promise.all(matchesWithUrls).then(results => {
                        setDatabaseMatches(results);
                    }).catch(error => {
                        console.error('Error getting signed URLs for database matches:', error);
                        setDatabaseMatches(databaseMatchesFound);
                    });
                }
            }
            
            // Set TV state if tags contain Television
            if ((currentRow.tags || []).some((t: string) => t.toLowerCase().includes('television'))) {
                setIsTV(true);
            }
        } else {
            // Reset to initial values if no currentRow
            setFormData({ ...INITIAL_FORM_VALUES });
            setIsTV(false);
        }
    }, [currentRow, totalScripts, currentFileList]);

    return hasLists ? (
        <Container fluid className="p-3">
            <Row>
                <Col xs={12}>
                    <h1
                        className="fs-5 text-break mb-3"
                        title={currentRow.file_name}
                    >
                        <FileNameForceWrap filename={currentRow.file_name as string} />
                    </h1>
                </Col>
            </Row>
            <Row className="g-3">
                <Col lg={6} xs={12} className="pe-lg-3">
                    <Form noValidate>
                    <Form.Group as={Row} className="mb-2">
                        <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Title *</Form.Label>
                        <Col md={10} xs={12}>
                            <Form.Control
                                name="script_title"
                                className={validated 
                                    ? (!formData.script_title || formData.script_title.trim() === '') 
                                        ? 'mt-0 is-invalid' 
                                        : 'mt-0 is-valid'
                                    : 'mt-0'}
                                placeholder="The script or document title"
                                value={formData.script_title}
                                onChange={handleInputChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Please provide a script title.
                            </Form.Control.Feedback>
                            {indexingMatches.length > 0 && (
                                <div className="mt-1">
                                    <small>
                                        <strong>
                                            This is an <span className="text-danger">exact duplicate</span> of {
                                                indexingMatches.length === 1 ?
                                                    `${indexingMatches.length} other non-indexed file` :
                                                    `${indexingMatches.length} other non-indexed files`
                                            }:
                                        </strong>
                                        <br />
                                        {indexingMatches.map((match, index) => (
                                            <span key={index}>
                                                {match.signed_url !== null ? (
                                                    <a
                                                        href={match.signed_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={match.file_name}
                                                    >
                                                        <FileNameForceWrap filename={match.file_name} />
                                                    </a>
                                                ) : (
                                                    <span className="text-danger"><FileNameForceWrap filename={match.file_name} /> (File Missing/Invalid)</span>
                                                )}
                                                {index < indexingMatches.length - 1 && <br />}
                                            </span>
                                        ))}
                                    </small>
                                </div>
                            )}
                            {databaseMatches.length > 0 && (
                                <div className="mt-1">
                                    <small>
                                        <strong>
                                            This file is an <span className="text-danger">exact duplicate</span> of {
                                                databaseMatches.length === 1 ?
                                                    `${databaseMatches.length} existing indexed file` :
                                                    `${databaseMatches.length} existing indexed files`
                                            }:
                                        </strong>
                                        <br />
                                        {databaseMatches.map((match, index) => (
                                            <span key={index}>
                                                {match.signed_url !== null ? (
                                                    <a
                                                        href={match.signed_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-break"
                                                        title={match["Title"]}
                                                    >
                                                        {match["Script Title"] || <FileNameForceWrap filename={match["Title"]} />}
                                                    </a>
                                                ) : (
                                                    <span className="text-danger text-break">{match["Script Title"]} (File Missing/Invalid)</span>
                                                )}
                                                {match["Writer(s)"] ? ` by ${match["Writer(s)"]}` : ''}
                                                {match["Draft Date"] ? ` (Draft Date: ${match["Draft Date"]})` : ''}
                                                {match["additional_info_concat"] ? ` - ${match["additional_info_concat"]}` : ''}
                                                {index < databaseMatches.length - 1 && <br />}
                                            </span>
                                        ))}
                                    </small>
                                </div>
                            )}
                            {titleMatches.length > 0 && (
                                <div className="mt-1">
                                    <small>
                                        <strong>Possible matches for existing database titles:</strong><br/>
                                        {titleMatches.map((match, index) => (
                                            <span key={index}>
                                                {match.signed_url !== null ? (
                                                    <a 
                                                        href={match.signed_url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        title={match["Title"]}
                                                    >
                                                        {match["Script Title"]}
                                                    </a>
                                                ) : (
                                                    <span className="text-danger">{match["Script Title"]} (File Missing/Invalid)</span>
                                                )}
                                                {match["Writer(s)"] ? ` by ${match["Writer(s)"]}` : ''}
                                                {match["Draft Date"] ? ` (Draft Date: ${match["Draft Date"]})` : ''}
                                                {match["additional_info_concat"] ? ` - ${match["additional_info_concat"]}` : ''}
                                                {index < titleMatches.length - 1 && <br/>}
                                            </span>
                                        ))}
                                    </small>
                                </div>
                            )}
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-2">
                        <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">{`Writer(s) *`}</Form.Label>
                        <Col md={10} xs={12}>
                            <Form.Control
                                name="script_writers"
                                className={validated 
                                    ? (!formData.script_writers || formData.script_writers.trim() === '') 
                                        ? 'mt-0 is-invalid' 
                                        : 'mt-0 is-valid'
                                    : 'mt-0'}
                                placeholder="The names of one or more writers"
                                value={formData.script_writers}
                                onChange={handleInputChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Please provide the names of one or more writers.
                            </Form.Control.Feedback>
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-2">
                        <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Draft Date</Form.Label>
                        <Col md={10} xs={12}>
                            <Form.Control
                                type="date"
                                name="draft_date"
                                className={validated 
                                    ? formData.draft_date && !Indexing.validateDate(formData.draft_date)
                                        ? 'mt-0 w-auto is-invalid' 
                                        : formData.draft_date
                                            ? 'mt-0 w-auto is-valid'
                                            : 'mt-0 w-auto'
                                    : 'mt-0 w-auto'}
                                value={formData.draft_date}
                                onChange={handleInputChange}
                            />
                            {validated && formData.draft_date && !Indexing.validateDate(formData.draft_date) && (
                                <div className="invalid-feedback d-block">
                                    Draft date can't be before 1900 or in the future.
                                </div>
                            )}
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-2">
                        <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Document Type</Form.Label>
                        <Col md={4} xs={12}>
                            <Typeahead
                                allowNew={true}
                                flip={true}
                                maxHeight="200px"
                                inputProps={{ className: "mt-0" }}
                                placeholder="Outline, Pitch Deck, etc."
                                selected={formData.document_type ? [formData.document_type] : []}
                                options={lists['Document Type'] ?? []}
                                onChange={(e) => {
                                    e.length > 0 ?
                                        handleInputChange({ target: { name: 'document_type', value: e[0] } }) :
                                        handleInputChange({ target: { name: 'document_type', value: '' } });
                                    }
                                }
                                onBlur={(e) => {
                                    const inputValue = e.target.value;
                                    if (inputValue && (!formData.document_type || formData.document_type !== inputValue)) {
                                        handleInputChange({ target: { name: 'document_type', value: inputValue } });
                                    }
                                }}
                            />
                        </Col>
                        <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Version</Form.Label>
                        <Col md={4} xs={12}>
                            <Typeahead
                                allowNew={true}
                                flip={true}
                                maxHeight="200px"
                                inputProps={{ className: "mt-0" }}
                                placeholder="First Draft, Blue, etc."
                                selected={formData.document_version ? [formData.document_version] : []}
                                options={lists['Draft'] ?? []}
                                onChange={(e) =>{
                                    e.length > 0 ?
                                        handleInputChange({ target: { name: 'document_version', value: e[0] } }) :
                                        handleInputChange({ target: { name: 'document_version', value: '' } });
                                    }
                                }
                                onBlur={(e) => {
                                    const inputValue = e.target.value;
                                    if (inputValue && (!formData.document_version || formData.document_version !== inputValue)) {
                                        handleInputChange({ target: { name: 'document_version', value: inputValue } });
                                    }
                                }}
                            />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-2">
                        <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Alt. Titles</Form.Label>
                        <Col md={10} xs={12}>
                            <Form.Control
                                className="mt-0 mb-3"
                                name="alternate_titles"
                                placeholder="AKA or FKA titles, if any"
                                value={formData.alternate_titles}
                                onChange={handleInputChange}
                            />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-2">
                        <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Notes</Form.Label>
                        <Col md={10} xs={12}>
                            <Form.Control
                                className="mt-0 mb-3"
                                name="document_notes"
                                placeholder="Any relevant notes or callouts"
                                value={formData.document_notes}
                                onChange={handleInputChange}
                            />
                            {additionalInfo !== "" && (
                                <Row className="mt-1 mb-2">
                                    <small>
                                        <strong>Additional Info:</strong> {additionalInfo}
                                    </small>
                                </Row>
                            )}
                        </Col>
                    </Form.Group>
                    {(() => {
                        const catFolder    = tagFolders.find(f => f.name === 'Category');
                        const indFolder    = tagFolders.find(f => f.name === 'Industry List');
                        const yrFolder     = tagFolders.find(f => f.name === 'Year');
                        const genreFolder  = tagFolders.find(f => f.name === 'Genre');
                        const tags = formData.tags || [];
                        const selCat   = tags.find((t: string) => catFolder?.tags.includes(t)) || '';
                        const selInd   = tags.find((t: string) => indFolder?.tags.includes(t)) || '';
                        const selYr    = tags.find((t: string) => yrFolder?.tags.includes(t)) || '';
                        const selGenre = tags.filter((t: string) => genreFolder?.tags.includes(t));
                        const selPilot = tags.includes('Pilot');

                        const updateFolderTag = (folderTags: string[], newVal: string) => {
                            const withoutFolder = tags.filter((t: string) => !folderTags.includes(t));
                            return newVal ? [...withoutFolder, newVal] : withoutFolder;
                        };

                        return (<>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Category</Form.Label>
                                <Col md={4} xs={12}>
                                    <Typeahead
                                        flip maxHeight="200px"
                                        placeholder="Film, Television, etc."
                                        inputProps={{ className: 'mt-0' }}
                                        selected={selCat ? [selCat] : []}
                                        options={catFolder?.tags ?? []}
                                        onChange={(e) => {
                                            const val = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : '';
                                            const newTags = updateFolderTag(catFolder?.tags || [], val);
                                            const withoutPilot = val.toLowerCase().includes('television') ? newTags : newTags.filter((t: string) => t !== 'Pilot');
                                            setFormData(prev => ({ ...prev, tags: withoutPilot }));
                                            setIsTV(val.toLowerCase().includes('television'));
                                            if (validated) setValidated(false);
                                        }}
                                        onBlur={(e) => {
                                            const v = e.target.value;
                                            if (v && v !== selCat) {
                                                const newTags = updateFolderTag(catFolder?.tags || [], v);
                                                setFormData(prev => ({ ...prev, tags: newTags }));
                                                setIsTV(v.toLowerCase().includes('television'));
                                            }
                                        }}
                                    />
                                </Col>
                                <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Industry List</Form.Label>
                                <Col md={4} xs={12}>
                                    <Typeahead
                                        flip maxHeight="200px"
                                        placeholder="WGA, DGA, etc."
                                        inputProps={{ className: 'mt-0' }}
                                        selected={selInd ? [selInd] : []}
                                        options={indFolder?.tags ?? []}
                                        onChange={(e) => {
                                            const val = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : '';
                                            setFormData(prev => ({ ...prev, tags: updateFolderTag(indFolder?.tags || [], val) }));
                                            if (validated) setValidated(false);
                                        }}
                                        onBlur={(e) => {
                                            const v = e.target.value;
                                            if (v && v !== selInd) setFormData(prev => ({ ...prev, tags: updateFolderTag(indFolder?.tags || [], v) }));
                                        }}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Yearly List</Form.Label>
                                <Col md={4} xs={12}>
                                    <Typeahead
                                        flip maxHeight="200px"
                                        placeholder="2024, etc."
                                        inputProps={{ className: 'mt-0' }}
                                        selected={selYr ? [selYr] : []}
                                        options={yrFolder?.tags ?? []}
                                        onChange={(e) => {
                                            const val = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : '';
                                            setFormData(prev => ({ ...prev, tags: updateFolderTag(yrFolder?.tags || [], val) }));
                                            if (validated) setValidated(false);
                                        }}
                                        onBlur={(e) => {
                                            const v = e.target.value;
                                            if (v && v !== selYr) setFormData(prev => ({ ...prev, tags: updateFolderTag(yrFolder?.tags || [], v) }));
                                        }}
                                    />
                                </Col>
                                {(userData?.isApprover || (userData as any)?.isTagger || userData?.isIndexer) && (<>
                                    <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Genre</Form.Label>
                                    <Col md={4} xs={12}>
                                        <Typeahead
                                            flip maxHeight="200px"
                                            placeholder="Drama, Comedy, etc."
                                            multiple
                                            selected={selGenre}
                                            options={genreFolder?.tags ?? []}
                                            onChange={(e) => {
                                                const vals = e.map(v => typeof v === 'string' ? v : (v as any).label || '');
                                                setFormData(prev => ({ ...prev, tags: [...(prev.tags || []).filter((t: string) => !genreFolder?.tags.includes(t)), ...vals] }));
                                                if (validated) setValidated(false);
                                            }}
                                            renderToken={(option, { onRemove }, idx) => (
                                                <span key={idx} className="badge bg-secondary d-inline-flex align-items-center gap-1 me-1 mb-1 fw-normal">
                                                    {option as string}
                                                    <button type="button" onClick={() => onRemove(option)} aria-label="Remove" style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1, color: 'inherit', opacity: 0.7, cursor: 'pointer', fontSize: '0.75rem' }}>×</button>
                                                </span>
                                            )}
                                        />
                                        <div className="d-flex align-items-center gap-2 mt-1">
                                            <Button variant="link" size="sm" className="p-0 text-secondary" style={{ fontSize: '0.75rem' }}
                                                onClick={async () => {
                                                    const title = formData.script_title || '';
                                                    if (!title) { showToast('info', 'Enter a title first.'); return; }
                                                    const genres = await fetchGenresFromWikipedia(title, isTV);
                                                    if (genres.length === 0) { showToast('info', 'No Wikipedia match found.'); return; }
                                                    setFormData(prev => ({ ...prev, tags: [...(prev.tags || []).filter((t: string) => !genreFolder?.tags.includes(t)), ...genres] }));
                                                }}
                                            >Suggest genres from Wikipedia</Button>
                                            <span className="text-muted" style={{ fontSize: '0.7rem' }}>— may be inaccurate, please verify</span>
                                        </div>
                                    </Col>
                                </>)}
                            </Form.Group>
                        </>);
                    })()}
                    {currentRow.curator_comments && (
                        <Row className="mb-2">
                            <Col md={3} xs={12} className="pt-0">Curator Comments</Col>
                            <Col md={9} xs={12}>
                                {currentRow.curator_comments}
                            </Col>
                        </Row>
                    )}
                    {isTV && (
                        <Form.Group as={Row} className="mb-2">
                            <Form.Label as={Col} md={2} xs={6} className="col-form-label pt-0">Pilot</Form.Label>
                            <Col md={4} xs={6}>
                                <Form.Check inline type="radio" id="pilot_yes" label="Yes"
                                    checked={(formData.tags || []).includes('Pilot')}
                                    onChange={() => setFormData(prev => ({ ...prev, tags: [...(prev.tags || []).filter((t: string) => t !== 'Pilot'), 'Pilot'] }))}
                                />
                                <Form.Check inline type="radio" id="pilot_no" label="No"
                                    checked={!(formData.tags || []).includes('Pilot')}
                                    onChange={() => setFormData(prev => ({ ...prev, tags: (prev.tags || []).filter((t: string) => t !== 'Pilot') }))}
                                />
                            </Col>
                            <Form.Label as={Col} md={2} xs={6} className="col-form-label pt-0">Episode #</Form.Label>
                            <Col md={4} xs={6}>
                                <Form.Control
                                    className="mt-0 mb-3"
                                    name="episode_number"
                                    placeholder="1x01, etc."
                                    value={formData.episode_number}
                                    onChange={handleInputChange}
                                />
                            </Col>
                        </Form.Group>
                    )}
                    <Row>
                        <small><strong>Note:</strong> Fields marked with an asterisk * are required. All other fields are optional and can be blank.</small>
                    </Row>
                    </Form>
                    <Stack direction="horizontal" gap={3} className="mt-3 d-flex flex-wrap">
                        <Button
                            variant="secondary"
                            className="flex-grow-1"
                            onClick={async () => {
                                setShowForm(false);
                                setParentLoadingState(true);
                                // Clear all form fields by preparing empty data
                                const clearFormData = Indexing.prepareFormDataForDatabase(INITIAL_FORM_VALUES);
                                await Indexing.saveIndex(currentRow, clearFormData, '', true);
                                resetForm();
                                if (refreshFileList) {
                                    await refreshFileList();
                                };
                            }}>
                            Cancel (Check In)
                        </Button>
                        <Button 
                            variant="primary"
                            className="flex-grow-1"
                            onClick={async () => {
                                setShowForm(false);
                                setParentLoadingState(true);
                                const preparedFormData = Indexing.prepareFormDataForDatabase(formData);
                                await Indexing.saveIndex(currentRow, preparedFormData);
                                resetForm();
                                if (refreshFileList) {
                                    await refreshFileList();
                                };
                            }}>
                            Save
                        </Button>
                        <Button
                            variant="outline-danger"
                            className="flex-grow-1"
                            onClick={() => {
                                setConfirmationMessage(`Are you sure you want to mark ${truncateMiddle(currentRow.file_name)} as a duplicate?`);
                                setPendingAction(() => async () => {
                                    setShowModal(false);
                                    setShowForm(false);
                                    setParentLoadingState(true);
                                    const preparedFormData = Indexing.prepareFormDataForDatabase(formData);
                                    await Indexing.saveIndex(currentRow, preparedFormData, 'Duplicate File');
                                    resetForm();
                                    if (refreshFileList) {
                                        await refreshFileList();
                                    }
                                });
                                setShowModal(true);
                            }}>
                            Duplicate
                        </Button>
                        <Button
                            variant="outline-danger"
                            className="flex-grow-1"
                            onClick={() => {
                                setConfirmationMessage(`Are you sure you want to mark ${truncateMiddle(currentRow.file_name)} as corrupt?\n\nSome files cannot be previewed on this page and must be downloaded first.`);
                                setShowDownloadLink(true);
                                setPendingAction(() => async () => {
                                    setShowModal(false);
                                    setShowForm(false);
                                    setParentLoadingState(true);
                                    const preparedFormData = Indexing.prepareFormDataForDatabase(formData);
                                    await Indexing.saveIndex(currentRow, preparedFormData, 'Corrupt File');
                                    resetForm();
                                    setShowDownloadLink(false);
                                    if (refreshFileList) {
                                        await refreshFileList();
                                    }
                                });
                                setShowModal(true);
                            }}>
                            Corrupt
                        </Button>
                        <Button
                            variant="outline-danger"
                            className="flex-grow-1"
                            onClick={() => {
                                setConfirmationMessage(`Are you sure that ${truncateMiddle(currentRow.file_name)} should be rejected?\n\nOnly use this option for document types that we don't accept.`);
                                setPendingAction(() => async () => {
                                    setShowModal(false);
                                    setShowForm(false);
                                    setParentLoadingState(true);
                                    const preparedFormData = Indexing.prepareFormDataForDatabase(formData);
                                    await Indexing.saveIndex(currentRow, preparedFormData, "Reject File");
                                    resetForm();
                                    if (refreshFileList) {
                                        await refreshFileList();
                                    }
                                });
                                setShowModal(true);
                            }}>
                            Reject File
                        </Button>
                        <Button 
                            variant="primary"
                            className="flex-grow-1"
                            onClick={async () => {
                                setValidated(true);
                                
                                if (!validateForm()) {
                                    const requiredFieldsValid = ['script_title', 'script_writers', 'category'].every(field => {
                                        const value = formData[field];
                                        return value && value.trim() !== '';
                                    });
                                    
                                    if (!requiredFieldsValid) {
                                        showToast("error", "Please fill in all required fields (Title, Writer(s), Category)");
                                    } else if (formData.draft_date && !Indexing.validateDate(formData.draft_date)) {
                                        showToast("error", "Draft date can't be before 1900 or in the future.");
                                    }
                                    return;
                                }

                                const preparedFormData = Indexing.prepareFormDataForDatabase(formData);

                                const handlePublish = async (matchToPublish?: any) => {
                                    setRequestMatches([]);
                                    setShowRequestModal(false);
                                    setShowForm(false);
                                    setParentLoadingState(true);
                                    await Indexing.publishToDatabase(currentRow, preparedFormData, matchToPublish);
                                    getScripts().then((data) => {
                                        if (data) {
                                            setTotalScripts(data as []);
                                            setTotalScriptsCount(data.length);
                                        }
                                    });
                                    resetForm();
                                    setValidated(false);
                                    if (refreshFileList) {
                                        await refreshFileList();
                                    }
                                };

                                const matches = await Indexing.getRequestMatches(formData.script_title as string, formData.script_writers as string);

                                if (matches.length > 0) {
                                    setRequestMatches(matches);
                                    setPendingAction(() => handlePublish);
                                    setShowRequestModal(true);
                                } else {
                                    await handlePublish();
                                }
                            }}
                        >
                            Publish
                        </Button>
                    </Stack>
                </Col>
                <Col lg={6} xs={12} className="d-flex">
                    <div className="border rounded overflow-hidden flex-grow-1 pdf-viewer">
                        {fileType === 'pdf' ? (
                            <PDFViewer fileUrl={url} fileName={currentRow.file_name} fileId={currentRow.file_id} allowDownload={true} />
                        ) : (
                            <AltViewer fileUrl={url} fileName={currentRow.file_name} />
                        )}
                    </div>
                </Col>
            </Row>
            <ConfirmationModal
                show={showModal}
                message={confirmationMessage}
                onConfirm={pendingAction}
                onHide={() => {
                    setShowModal(false);
                    setPendingAction(null);
                    setShowDownloadLink(false);
                }}
                linkDescription={showDownloadLink ? currentRow.file_name : null}
                linkUrl={showDownloadLink ? url : null}
            />
            <RequestMatchConfirmationModal
                matches={requestMatches}
                show={showRequestModal}
                onConfirm={(matchValue) => {
                    pendingAction?.(matchValue);
                    setPendingAction(null);
                }}
                onHide={() => {
                    setShowRequestModal(false);
                    pendingAction?.();
                    setPendingAction(null);
                }}
            />
        </Container>
    ) : (<LoadingRing />);
}